/**
 * Styled Tag Component - Odoo-inspired design
 *
 * Features:
 * - Soft pastel colors with opacity overlay
 * - Rounded pill shape
 * - Better padding and spacing
 * - Matching text color to tag color
 */

import { CSSProperties } from 'react';

interface StyledTagProps {
  name: string;
  color: string;
  style?: CSSProperties;
  onClose?: () => void;
  closable?: boolean;
}

/**
 * Lightens a hex color by adding opacity
 */
function getLighterColor(hexColor: string, opacity: number = 0.2): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Return rgba with opacity
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Darkens a color for better text contrast
 */
function getDarkerColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - 40);
  const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - 40);
  const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - 40);
  return `rgb(${r}, ${g}, ${b})`;
}

export default function StyledTag({
  name,
  color,
  style,
  onClose,
  closable = false,
}: StyledTagProps) {
  const baseStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: 12,
    backgroundColor: getLighterColor(color, 0.2),
    color: getDarkerColor(color),
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '16px',
    border: 'none',
    whiteSpace: 'nowrap',
    marginRight: 4,
    marginBottom: 4,
    ...style,
  };

  return (
    <span style={baseStyle}>
      {name}
      {closable && onClose && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          style={{
            marginLeft: 6,
            cursor: 'pointer',
            fontWeight: 'bold',
            opacity: 0.6,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.6';
          }}
        >
          ×
        </span>
      )}
    </span>
  );
}

// Helper functions exported for use in other components
export { getLighterColor, getDarkerColor };
